import React from "react";
import inflection from "inflection";

import { Tooltip, Typography } from "@material-ui/core";
import { orange } from "@material-ui/core/colors";

import {
  DAMAGED_COLOR,
  DamagedIcon,
  ExceptionIcon,
  ReturnIcon,
  WarningIcon,
} from "../components/common/iconography";
import {
  getCustomFieldById,
  getCustomFieldId,
  parseCustomField,
} from "../components/common/CustomFields";
import { parsePath } from "history";

import { logError, logHistory as logHistoryNote } from "./appUtils";
import {
  NoteType,
  Priorities,
  TaskStatus,
  InventoryStatus,
  InboundTrackSteps,
  InboundState,
  UNSPOTTED_TRACK_ID,
  Unit,
  AstTaxCode,
  LocationType,
} from "./constants";
import { isRailShipmentInTrackArea, isUnmanagedAisle } from "./locationUtils";
import { getUnitName, isBulkCommodity } from "./loadTypeUtils";
import { getPhotoUrl } from "./fileUtils";
import { formatInYardTimeZone } from "./dateUtils";

export const getInventoryQuantity = (location, currentInventory) => {
  const inventoryLoadType =
    currentInventory.reloadYardCommodity.commodity.locationLoadType;
  const locationLoadType = location.locationLoad[inventoryLoadType];
  if (!location.capacityUsed) location.capacityUsed = 0;
  const quantity = location.capacityUsed / locationLoadType.capacity;
  return {
    quantity,
    name: `${quantity} ${getUnitName(currentInventory.inventoryUOM)}`,
  };
};

// Utility function to split an array into chunks of a given size
export const chunkArray = (array, size) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

/**
 * Normalize a value into an uppercase alphanumeric-only string.
 * Removes spaces, punctuation, underscores, dashes, and any other
 * non-letter/non-number characters before uppercasing the result.
 *
 * @param {string} value
 * @returns {string}
 */
export const normalizeValue = (value) => {
  return (
    String(value || "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase() || " "
  );
};

/**
 * Core flat redirect — performs a simple navigation without altering breadcrumbs.
 *
 * Behavior:
 * - Navigates to the provided `redirectTo` path.
 * - Merges `state` and `backPath` into the new location state.
 * - Does NOT append the current path or modify the breadcrumb trail.
 *
 * Intended use:
 * - For “flat” navigations where the breadcrumb should remain unchanged.
 * - Used internally by shallowRedirect and deepRedirect for consistency.
 *
 * @param {object} history - A history-like object (e.g., from React Router).
 * @param {string} redirectTo - The destination path.
 * @param {string[]} [nextBackPath] - The breadcrumb trail to preserve.
 * @param {object} [state] - Optional state merged into the navigation.
 */
export const flatRedirect = (
  history,
  redirectTo,
  nextBackPath = [],
  state = {},
) => {
  // Merge provided state with breadcrumb trail (non-mutating)
  const nextState = { ...state, backPath: [...nextBackPath] };

  // Perform navigation using React Router's history.push
  history.push({
    ...parsePath(redirectTo),
    state: nextState,
  });
};

/**
 * Shallow redirect — navigates without appending the current path.
 *
 * Behavior:
 * - Supports both string and array `redirectPath`:
 *   - Array form: last element is the destination, the rest form the breadcrumb.
 *   - String form: uses `backPath` (if provided) as breadcrumb.
 * - If the destination equals the last breadcrumb entry, pops it off
 *   (treats it as a true “back” navigation).
 * - Does NOT append the current path — unlike deepRedirect.
 * - Non-mutating: clones arrays, merges state cleanly.
 *
 * @param {object} history - A history-like object (e.g., from React Router).
 * @param {string|string[]} redirectPath - Destination path or breadcrumb array.
 * @param {string[]} [backPath] - Existing breadcrumb (used if redirectPath is a string).
 * @param {object} [state] - Optional navigation state.
 */
export const shallowRedirect = (history, redirectPath, backPath, state) => {
  // Resolve target destination and breadcrumb
  const redirectTo = Array.isArray(redirectPath)
    ? redirectPath[redirectPath.length - 1]
    : redirectPath;

  // Determine breadcrumb trail from input
  let nextBackPath = Array.isArray(redirectPath)
    ? redirectPath.slice(0, -1) // Array carries its own breadcrumb
    : Array.isArray(backPath)
      ? [...backPath] // Clone provided backPath
      : [];

  // If the destination is already the last breadcrumb entry, pop it
  const last = nextBackPath[nextBackPath.length - 1];
  if (last === redirectTo) {
    nextBackPath = nextBackPath.slice(0, -1);
  }

  // Delegate to flatRedirect for consistent navigation
  flatRedirect(history, redirectTo, nextBackPath, state);
};

/**
 * Deep redirect — navigates forward and grows the breadcrumb trail.
 *
 * Behavior:
 * - Builds upon shallowRedirect and flatRedirect.
 * - If redirectPath is an array:
 *   - The last element is the destination, the rest are breadcrumbs.
 * - If redirectPath is a string:
 *   - Uses provided `backPath` (or empty) as breadcrumb.
 *   - Adds the current path to breadcrumb unless it’s already the last one.
 * - Detects and handles “back” navigations:
 *   - If current path equals the last breadcrumb, performs shallowRedirect (no growth).
 *   - If destination equals last breadcrumb, also shallowRedirects (pops last).
 * - Always non-mutating and merges navigation state properly.
 *
 * @param {object} history - A history-like object (e.g., from React Router).
 * @param {string|string[]} redirectPath - Target destination or breadcrumb array.
 * @param {string[]} [backPath] - Existing breadcrumb (used if redirectPath is a string).
 * @param {object} [state] - Optional navigation state.
 */
export const deepRedirect = (history, redirectPath, backPath, state) => {
  const currentPath = history.location.pathname + history.location.search;

  let redirectTo;
  let nextBackPath;

  if (Array.isArray(redirectPath)) {
    // Array form → last is destination, rest form breadcrumb
    redirectTo = redirectPath[redirectPath.length - 1];
    nextBackPath = redirectPath.slice(0, -1);
  } else {
    // String form → use backPath or empty array
    redirectTo = redirectPath;
    nextBackPath = Array.isArray(backPath) ? [...backPath] : [];

    const last = nextBackPath[nextBackPath.length - 1];

    // Case 1: If current path is already last breadcrumb → shallow redirect
    if (last === currentPath) {
      return shallowRedirect(history, redirectTo, nextBackPath, state);
    }

    // Case 2: If redirect target equals last breadcrumb → pop and shallow redirect
    if (last === redirectTo) {
      return shallowRedirect(
        history,
        redirectTo,
        nextBackPath.slice(0, -1),
        state,
      );
    }

    // Case 3: Otherwise, push the current path to breadcrumb (deep navigation)
    nextBackPath.push(currentPath);
  }

  // Delegate final navigation to flatRedirect
  flatRedirect(history, redirectTo, nextBackPath, state);
};

export const getBackPath = (props) => {
  let backPath = props.location?.state?.backPath;
  return backPath ? backPath : [props.basePath];
};

/**
 * Trim backPath by removing trailing entries that include the full record ID.
 * Ensures at least one path (base path) remains.
 *
 * @param {string[]} backPath - The original backPath array.
 * @param {object} record - The record object with an `id` field.
 * @returns {string[]} - The trimmed backPath array.
 */
export function trimBackPath(backPath, record) {
  if (!Array.isArray(backPath) || !record?.id) return [];
  const trimmed = [...backPath];
  while (
    trimmed.length > 1 &&
    trimmed[trimmed.length - 1]?.includes(record.id)
  ) {
    trimmed.pop();
  }
  return trimmed;
}

export const getFormTab = (props) => {
  const { location } = props;
  const lastSegment = location.pathname.split("/").filter(Boolean).pop();

  // If the last segment is a number, treat it as an index; otherwise, it's a tab name.
  const isNumericTab = /^\d+$/.test(lastSegment);

  return isNumericTab
    ? lastSegment
    : lastSegment === props.match?.params?.id
      ? "0"
      : lastSegment;
};

export const getInventoryPrimaryField = (
  account,
  inventory,
  lineItemInventoryItem,
) => {
  if (account.inventoryPrimaryField) {
    let primaryField = inventory[account.inventoryPrimaryField];
    if (primaryField) {
      if (account.inventoryPrimaryField === "lotNumber")
        return { label: "Lot Number", value: primaryField };
      if (account.inventoryPrimaryField === "poNumber")
        return { label: "PO Number", value: primaryField };
    } else {
      primaryField = getCustomFieldById(
        inventory.customFields,
        account.inventoryPrimaryField,
      );
      if (primaryField?.value) {
        return { label: primaryField.name, value: primaryField.value };
      }
    }
  }
  // Chained all the way down: an item on the Unspotted virtual track has no
  // Location record, so `location` is null rather than merely absent.
  if (
    lineItemInventoryItem?.inventoryItem?.location?.locationLoad?.container
      ?.selected
  ) {
    return { label: "Container", value: inventory.productName };
  }
  return { label: "Lot Number", value: inventory.lotNumber };
};

export const getLogState = (state) => {
  return state ? `| state: ${state}` : "";
};

export const isEmptyAddress = (address) => {
  // Check if address is undefined, null, or if all relevant fields are falsy
  return (
    !address ||
    (!address.name &&
      !address.street &&
      !address.city &&
      !address?.state &&
      !address.zipCode &&
      !address.country)
  );
};

export function isFullAddress(address) {
  return (
    address?.name &&
    address?.street &&
    address?.city &&
    address?.state &&
    address?.zipCode &&
    address?.country
  );
}

/**
 * The six postal fields that make two entries the same address — name, street,
 * city, state, zip and country — trimmed and case-folded into one comparable
 * key.
 *
 * Phone is deliberately outside the key. The address-book import counts the
 * same yard listed with and without a dispatch number as one address, while
 * isAddressEqual below adds phone on top for the callers matching a shipment's
 * ship-to against a saved entry field for field. Both read the postal fields
 * from here, so "the same address" cannot come to mean two different things:
 * a normalisation added for one (street suffixes, say) reaches the other.
 */
export function addressKey(address) {
  return [
    address?.name,
    address?.street,
    address?.city,
    address?.state,
    address?.zipCode,
    address?.country,
  ]
    .map((part) =>
      String(part ?? "")
        .trim()
        .toLowerCase(),
    )
    .join("|");
}

export function isAddressEqual(addr1, addr2) {
  if (!addr1 || !addr2) return false;
  return (
    addressKey(addr1) === addressKey(addr2) &&
    (addr1.phone || "").trim() === (addr2.phone || "").trim()
  );
}

export const updateExceptionQuantity = (toUpdateInventory) => {
  const totalQuantityOnHand = (
    toUpdateInventory.inventoryItems?.items || []
  ).reduce(
    (total, inventoryItem) =>
      (total += Number(inventoryItem.quantityOnHand || 0)),
    0,
  );
  const quantityReceived = Number.isFinite(
    Number(toUpdateInventory.quantityReceived),
  )
    ? Number(toUpdateInventory.quantityReceived)
    : totalQuantityOnHand;
  const quantityAdvised = Number(toUpdateInventory.quantityAdvised || 0);
  const difference = trimDecimal(quantityReceived - quantityAdvised);

  // Crew inbound save is the canonical place for over/under shipment handling.
  // Keep exception flags and quantities aligned to the current advised vs received diff.
  if (difference > 0) {
    toUpdateInventory.overShipmentQuantity = difference;
  } else if (difference < 0) {
    toUpdateInventory.underShipmentQuantity = Math.abs(difference);
  }

  if (
    toUpdateInventory.isDamaged ||
    toUpdateInventory.overShipment ||
    toUpdateInventory.underShipment ||
    toUpdateInventory.otherException
  ) {
    toUpdateInventory.hasException = true;
  } else {
    toUpdateInventory.hasException = false;
  }
};

export const getInitQuantityOnHand = (
  inventory,
  inventoryItem,
  selectedAisle,
) => {
  let quantityOnHand = "";
  if (!inventory || !selectedAisle) return quantityOnHand;
  if (isBulkCommodity(inventory.reloadYardCommodity?.commodity)) {
    // ISO tank / BULK_MOBILE flow uses structured aisles in bulk area.
    // For new placements, default to full advised quantity (not advised-received remainder).
    if (
      selectedAisle?.type === LocationType.STRUCTURED &&
      !inventoryItem?.id &&
      !inventoryItem?.quantityOnHand
    ) {
      return trimDecimal(Number(inventory.quantityAdvised || 0));
    }
    if (inventoryItem.quantityOnHand) return inventoryItem.quantityOnHand;
    let quantityOnHand =
      inventory.quantityAdvised - (inventory.quantityReceived || 0);
    quantityOnHand = trimDecimal(quantityOnHand);
    if (quantityOnHand < 0) quantityOnHand = "";
    return quantityOnHand;
  }

  if (isUnmanagedAisle(selectedAisle)) {
    if (inventoryItem.quantityOnHand)
      quantityOnHand = inventoryItem.quantityOnHand;
    return quantityOnHand;
  }
  return 1;
};

export const handleNumberFieldKeyDown = (event) => {
  const key = event.key;
  const keyCode = event.keyCode || event.which; // Handle different browser behaviors
  const value = event.target.value;

  // Allow digits, backspace (8), delete (46), left arrow (37), right arrow (39), and decimal point (190, 110)
  if (
    !/^\d$/.test(key) &&
    keyCode !== 8 &&
    keyCode !== 46 &&
    keyCode !== 37 &&
    keyCode !== 39 &&
    keyCode !== 190 && // period (.)
    keyCode !== 110 // numpad period (.)
  ) {
    event.preventDefault();
    return;
  }

  // Prevent multiple decimal points
  if ((keyCode === 190 || keyCode === 110) && value.includes(".")) {
    event.preventDefault();
    return;
  }
};

export const getTaskStatusText = (id) => {
  return TaskStatus.find((itm) => itm.id === id)?.name;
};

export const getTaskPriorityText = (id) => {
  return Priorities.find((itm) => itm.id === id)?.name;
};

export const historyAutoGen = (
  form,
  data,
  ignoreList = [],
  includeArrayList = [],
  includeObjectList = [],
) => {
  const formState = form.getState();
  const newHistory = [];
  const dirtyFields = Object.keys(formState.dirtyFields);
  const dirtyCustomFields = dirtyFields.filter((itm) =>
    itm.startsWith("customFields"),
  );
  const includeArrayMap = includeArrayList.reduce(function (map, obj) {
    const parsed = obj.split(".");
    map[parsed[0]] = parsed[1];
    return map;
  }, {});

  debugger;
  try {
    dirtyFields.forEach((key) => {
      if (!ignoreList.includes(key)) {
        if (
          key.startsWith("customFields") &&
          includeArrayList.includes("customFields")
        ) {
          const fieldSata = form.getFieldState(key);
          const initState = parseCustomField(fieldSata.initial);
          const newState = parseCustomField(fieldSata.value);

          if (initState.name === newState.name) {
            if (newState.value !== initState.value) {
              const text = initState.value
                ? `${initState.name} changed from ${initState.value} to ${newState.value}`
                : `${initState.name} set to ${newState.value}`;
              const newNote = {
                text: text,
                type: NoteType.GENERIC,
                date: new Date(),
                by: window.appUser?.fullName,
              };
              newHistory.push(newNote);
            }
          } else {
            dirtyCustomFields.forEach((customKey) => {
              if (customKey !== key) {
                const customFieldSata = form.getFieldState(key);
                const customInitState = parseCustomField(
                  customFieldSata.initial,
                );
                const customNewState = parseCustomField(customFieldSata.value);

                if (customInitState.name === customNewState.name) {
                  if (newState.value !== initState.value) {
                    const text = customInitState.value
                      ? `${customInitState.name} changed from ${customInitState.value} to ${customNewState.value}`
                      : `${customInitState.name} set to ${customNewState.value}`;
                    const newNote = {
                      text: text,
                      type: NoteType.GENERIC,
                      date: new Date(),
                      by: window.appUser?.fullName,
                    };
                    newHistory.push(newNote);
                  }
                }
              }
            });
          }
        } else if (includeArrayMap[key]) {
          // Array
          const fieldName = includeArrayMap[key];
          const fullName = key + " " + fieldName;
          const label = inflection.transform(fullName, [
            "underscore",
            "humanize",
          ]);
          const fieldState = form.getFieldState(key);
          const initArray = fieldState.initial || [];
          const newArray = fieldState.value || [];
          const deltaDate = new Date();

          // added or edited
          const added = newArray.filter((x) => !initArray.includes(x));

          // deleted or edited
          const deleted = initArray.filter((x) => !newArray.includes(x));

          deleted.forEach((item) => {
            const newNote = {
              text: `${item[fieldName]} removed from ${label}`,
              type: NoteType.GENERIC,
              date: deltaDate,
              by: window.appUser?.fullName,
            };
            newHistory.push(newNote);
          });

          added.forEach((item) => {
            const newNote = {
              text: `${item[fieldName]} added to ${label}`,
              type: NoteType.GENERIC,
              date: deltaDate,
              by: window.appUser?.fullName,
            };
            newHistory.push(newNote);
          });
        } else if (key.includes(".") && !key.includes("]")) {
          // Object
          const keyValue = key.split(".")[0];
          if (includeObjectList.includes(keyValue)) {
            const fieldName = key.replaceAll(".", " ");
            const label = inflection.transform(fieldName, [
              "underscore",
              "humanize",
            ]);
            const fieldState = form.getFieldState(key);
            const initValue = fieldState.initial;
            const newValue = fieldState.value;
            debugger;

            if (newValue !== initValue) {
              const text = initValue
                ? `${label} changed from ${initValue} to ${newValue}`
                : `${label} set to ${newValue}`;
              const newNote = {
                text: text,
                type: NoteType.GENERIC,
                date: new Date(),
                by: window.appUser?.fullName,
              };
              newHistory.push(newNote);
            }
          }
        } else {
          // String
          const label = inflection.transform(key, ["underscore", "humanize"]);
          const initValue = formState.initialValues[key];
          const newValue = formState.values[key];

          if (
            typeof initValue === "string" ||
            typeof initValue === "number" ||
            typeof newValue === "string" ||
            typeof newValue === "number" ||
            typeof newValue === "boolean" ||
            typeof newValue === "boolean"
          ) {
            const text = initValue
              ? `${label} changed from ${initValue} to ${newValue}`
              : `${label} set to ${newValue}`;
            const newNote = {
              text: text,
              type: NoteType.GENERIC,
              date: new Date(),
              by: window.appUser?.fullName,
            };
            newHistory.push(newNote);
          }
        }
      }
    });
  } catch (err) {
    logError("admin:utils:utils:historyAutoGen", err, false);
  }

  if (newHistory?.length > 0) {
    if (data.history) data.history = [...data.history, ...newHistory];
    else data.history = newHistory;
  }
  return data.history;
};

const toHistoryDateTime = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

/**
 * History note for a shipment's collectedDateTime, which the dispatch tab
 * labels "Time Pulled".
 *
 * historyAutoGen cannot describe this field: the date picker writes a Date,
 * and its scalar pass only notes strings, numbers and booleans, so a first
 * stamp logged nothing and a change logged "Collected date time" against a
 * raw UTC ISO string. Callers put collectedDateTime on the ignore list and
 * append this instead, formatted in the yard's wall clock like the picker.
 *
 * @param {object} record - the shipment before the save
 * @param {object} data - the shipment being saved
 * @returns {object|null} a history note to append, or null when unchanged
 */
export const buildTimePulledNote = (record, data) => {
  const previous = toHistoryDateTime(record?.collectedDateTime);
  const next = toHistoryDateTime(data?.collectedDateTime);
  if (previous === next) return null;

  const label = (value) => formatInYardTimeZone(value, "MM/dd/yyyy hh:mm a");
  let text;
  if (!next) text = `Time Pulled cleared (was ${label(previous)})`;
  else if (!previous) text = `Time Pulled set to ${label(next)}`;
  else text = `Time Pulled changed from ${label(previous)} to ${label(next)}`;

  return {
    text,
    type: NoteType.GENERIC,
    date: new Date(),
    by: window.appUser?.fullName,
  };
};

/**
 * Automatically generates HistoryNote records from form changes and logs them asynchronously.
 * Similar to historyAutoGen but creates HistoryNote records and calls logHistory for each.
 *
 * @param {object} form - The react-final-form form instance
 * @param {string} parentId - The ID of the parent record (e.g., account.id)
 * @param {string} parentType - The type of parent record (e.g., "ACCOUNT", "PRODUCT")
 * @param {array} ignoreList - List of field names to ignore
 * @param {array} includeArrayList - List of array fields to include (e.g., ["customFields", "services.name"])
 * @param {array} includeObjectList - List of object fields to include (e.g., ["address", "billingAddress"])
 * @param {object} options - { log: false } returns the notes without logging them
 * @returns {array} Array of generated HistoryNote objects
 */
export const autogenHistoryNotes = (
  form,
  parentId,
  parentType,
  ignoreList = [],
  includeArrayList = [],
  includeObjectList = [],
  { log = true } = {},
) => {
  // With log false the notes are only returned, for a caller that logs them
  // once its save has succeeded.
  const logHistory = log ? logHistoryNote : () => {};
  const formState = form.getState();
  const newHistoryNotes = [];
  const dirtyFields = Object.keys(formState.dirtyFields);
  const dirtyCustomFields = dirtyFields.filter((itm) =>
    itm.startsWith("customFields"),
  );
  const includeArrayMap = includeArrayList.reduce(function (map, obj) {
    const parsed = obj.split(".");
    map[parsed[0]] = parsed[1];
    return map;
  }, {});

  try {
    dirtyFields.forEach((key) => {
      if (!ignoreList.includes(key)) {
        if (
          key.startsWith("customFields") &&
          includeArrayList.includes("customFields")
        ) {
          const fieldState = form.getFieldState(key);
          const initState = parseCustomField(fieldState.initial);
          const newState = parseCustomField(fieldState.value);

          if (initState.name === newState.name) {
            if (newState.value !== initState.value) {
              const text = initState.value
                ? `${initState.name} changed from ${initState.value} to ${newState.value}`
                : `${initState.name} set to ${newState.value}`;

              const historyNote = {
                parentId,
                parentType,
                text,
              };

              newHistoryNotes.push(historyNote);
              // Log history asynchronously
              logHistory(historyNote);
            }
          } else {
            dirtyCustomFields.forEach((customKey) => {
              if (customKey !== key) {
                const customFieldState = form.getFieldState(key);
                const customInitState = parseCustomField(
                  customFieldState.initial,
                );
                const customNewState = parseCustomField(customFieldState.value);

                if (customInitState.name === customNewState.name) {
                  if (newState.value !== initState.value) {
                    const text = customInitState.value
                      ? `${customInitState.name} changed from ${customInitState.value} to ${customNewState.value}`
                      : `${customInitState.name} set to ${customNewState.value}`;

                    const historyNote = {
                      parentId,
                      parentType,
                      text,
                    };

                    newHistoryNotes.push(historyNote);
                    // Log history asynchronously
                    logHistory(historyNote);
                  }
                }
              }
            });
          }
        } else if (includeArrayMap[key]) {
          // Array
          const fieldName = includeArrayMap[key];
          const fullName = key + " " + fieldName;
          const label = inflection.transform(fullName, [
            "underscore",
            "humanize",
          ]);
          const fieldState = form.getFieldState(key);
          const initArray = fieldState.initial || [];
          const newArray = fieldState.value || [];

          // added or edited
          const added = newArray.filter((x) => !initArray.includes(x));

          // deleted or edited
          const deleted = initArray.filter((x) => !newArray.includes(x));

          deleted.forEach((item) => {
            const text = `${item[fieldName]} removed from ${label}`;
            const historyNote = {
              parentId,
              parentType,
              text,
            };

            newHistoryNotes.push(historyNote);
            // Log history asynchronously
            logHistory(historyNote);
          });

          added.forEach((item) => {
            const text = `${item[fieldName]} added to ${label}`;
            const historyNote = {
              parentId,
              parentType,
              text,
            };

            newHistoryNotes.push(historyNote);
            // Log history asynchronously
            logHistory(historyNote);
          });
        } else if (key.includes(".") && !key.includes("]")) {
          // Object
          const keyValue = key.split(".")[0];
          if (includeObjectList.includes(keyValue)) {
            const fieldName = key.replaceAll(".", " ");
            const label = inflection.transform(fieldName, [
              "underscore",
              "humanize",
            ]);
            const fieldState = form.getFieldState(key);
            const initValue = fieldState.initial;
            const newValue = fieldState.value;

            if (newValue !== initValue) {
              const text = initValue
                ? `${label} changed from ${initValue} to ${newValue}`
                : `${label} set to ${newValue}`;

              const historyNote = {
                parentId,
                parentType,
                text,
              };

              newHistoryNotes.push(historyNote);
              // Log history asynchronously
              logHistory(historyNote);
            }
          }
        } else {
          // String/Number/Boolean
          const label = inflection.transform(key, ["underscore", "humanize"]);
          const initValue = formState.initialValues[key];
          const newValue = formState.values[key];

          if (
            typeof initValue === "string" ||
            typeof initValue === "number" ||
            typeof newValue === "string" ||
            typeof newValue === "number" ||
            typeof newValue === "boolean" ||
            typeof initValue === "boolean"
          ) {
            const text = initValue
              ? `${label} changed from ${initValue} to ${newValue}`
              : `${label} set to ${newValue}`;

            const historyNote = {
              parentId,
              parentType,
              text,
            };

            newHistoryNotes.push(historyNote);
            // Log history asynchronously
            logHistory(historyNote);
          }
        }
      }
    });
  } catch (err) {
    logError("admin:utils:utils:historyNoteAutoGen", err, false);
  }

  return newHistoryNotes;
};

export const exportCustomField = (field, target, customHeaderMap) => {
  const fieldValues = parseCustomField(field);
  const fieldName = fieldValues.name;

  if (customHeaderMap.has(fieldName)) {
    target[customHeaderMap.get(fieldName)] = fieldValues.value;
  } else {
    const columnId = getCustomFieldId(fieldName);
    customHeaderMap.set(fieldName, columnId);
    target[columnId] = fieldValues.value;
  }
};

export const splitEmails = (emailString, separator = /[,;\s]+/) => {
  if (emailString)
    return emailString
      .split(separator)
      .map((email) => email.trim())
      .filter((email) => email !== "");
  return null;
};

/**
 * Trimmed email string, or "" when blank. Imported accounts stored " " as a
 * placeholder billing email, so a plain truthiness check reads it as present.
 */
export const trimEmail = (emailString) => emailString?.trim() || "";

/**
 * Resolve the value of a token key from the record or global context.
 * - Supports nested keys (e.g., "customer.name" or "reloadYard.name").
 * - If the value is an array, join non-empty items with commas.
 * - Returns null if the value is missing, empty, or invalid.
 *
 * @param {string} tokenKey - Token key without braces, e.g. "invoiceNumber" or "poNumbers".
 * @returns {string|null}
 */
const resolveTokenValue = (record, tokenKey) => {
  // Special case: handle reloadYard.name from global appUser context
  if (tokenKey === "reloadYard.name") {
    return (
      record?.reloadYard?.name ?? window?.appUser?.reloadYard?.name ?? null
    );
  }

  // Traverse nested keys safely, e.g. "shipment.poNumbers"
  const rawValue = tokenKey
    .split(".")
    .reduce((obj, key) => obj?.[key] ?? null, record);

  // Handle array values: join with comma if any valid items exist
  if (Array.isArray(rawValue)) {
    const filtered = rawValue.filter((v) => v != null && v !== "");
    return filtered.length ? filtered.join(", ") : null;
  }

  // Return the value if non-null; otherwise, null
  return rawValue ?? null;
};

/**
 * Generate an email subject line from a structured subject template and a data record.
 * The subject template is an array of text and token objects, for example:
 * [
 *   { type: "text", value: "Invoice #:" },
 *   { type: "token", value: "{invoiceNumber}" },
 *   { type: "text", value: ", Order #:" },
 *   { type: "token", value: "{poNumber}" },
 *   { type: "text", value: "from" },
 *   { type: "token", value: "{reloadYard.name}" },
 * ]
 *
 * Each "token" element is replaced with a corresponding value from the record,
 * or from a global context (e.g., window.appUser.reloadYard.name).
 *
 * @param {string|Array} subjectTemplate - The stored subject definition (JSON string or array of elements).
 * @param {Object} record - The record object providing values for token replacement.
 * @returns {string} - A formatted subject line with tokens resolved and cleaned up.
 */
export const generateEmailSubject = (subjectTemplate, record) => {
  // Parse the subject definition safely.
  // - If the input is already an array, use it directly.
  // - If it's a JSON string, try to parse it.
  const subjectDef = (() => {
    if (typeof subjectTemplate === "string") {
      try {
        return JSON.parse(subjectTemplate);
      } catch {
        return [];
      }
    }
    return subjectTemplate || [];
  })();

  const parts = [];

  /**
   * Iterate through each element of the subject definition.
   * Handles the following cases:
   * - Text followed by a token → treat as a logical pair (skip both if token is empty)
   * - Standalone text → keep it as-is
   * - Standalone token → include it only if a valid value exists
   */
  for (let i = 0; i < subjectDef.length; i++) {
    const el = subjectDef[i];

    // --- Case 1: element is plain text ---
    if (el.type === "text") {
      const next = subjectDef[i + 1];

      // Check if next element is a token — a common "label + value" pattern
      if (next?.type === "token") {
        const tokenKey = next.value.replace(/[{}]/g, "");
        const tokenValue = resolveTokenValue(record, tokenKey);

        if (tokenValue) {
          // Both label and token are valid → include both
          parts.push(el.value, tokenValue);
        }
        i++; // Skip the next token since we've processed it
      } else {
        // Standalone text (not followed by a token)
        parts.push(el.value);
      }
    }

    // --- Case 2: element is a standalone token ---
    else if (el.type === "token") {
      const tokenKey = el.value.replace(/[{}]/g, "");
      const tokenValue = resolveTokenValue(record, tokenKey);
      if (tokenValue) parts.push(tokenValue);
    }
  }

  // Join with spaces to ensure separation.
  // Collapse multiple spaces into one.
  // Trim leading/trailing whitespace.
  const fullSubject = parts.join(" ").replace(/\s+/g, " ").trim();
  return fullSubject.substring(0, 998); // RFC 2822) allows up to 998 characters in a single line
};

/**
 * Normalize a lot number template into an array of template elements.
 * Templates are persisted as JSON strings on the account but handled as arrays
 * in the form state, so both shapes reach the generator.
 *
 * @param {string|Array} lotNumberTemplate - JSON string or array of template elements.
 * @returns {Array} - Template elements, or [] if missing or unparseable.
 */
export const parseLotNumberTemplate = (lotNumberTemplate) => {
  if (typeof lotNumberTemplate === "string") {
    try {
      const parsed = JSON.parse(lotNumberTemplate);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(lotNumberTemplate) ? lotNumberTemplate : [];
};

/**
 * Check whether a lot number template references a given record field, for
 * example "railCarNumber". Callers use this to hold a lot number at a
 * placeholder while a token the template depends on is still unknown.
 *
 * @param {string|Array} lotNumberTemplate - JSON string or array of template elements.
 * @param {string} tokenKey - Field name without braces, e.g. "railCarNumber".
 * @returns {boolean}
 */
export const lotTemplateUsesToken = (lotNumberTemplate, tokenKey) =>
  parseLotNumberTemplate(lotNumberTemplate).some(
    (element) =>
      element?.type === "token" &&
      element.value?.replace(/[{}]/g, "") === tokenKey,
  );

/**
 * Generate a lot number from a structured template and an inbound shipment record.
 * The template is an array of text and token objects, for example:
 * [
 *   { type: "text", value: "LOT-" },
 *   { type: "token", value: "{account.lotNumberPrefix}" },
 *   { type: "text", value: "-" },
 *   { type: "token", value: "{poNumber}" }
 * ]
 *
 * Behavior mirrors generateEmailSubject, except:
 * - Parts are concatenated without extra spaces.
 * - Text immediately followed by a token is omitted entirely if the token is empty.
 *
 * @param {string|Array} lotNumberTemplate - JSON string or array of template elements.
 * @param {Object} inboundShipment - The inbound shipment providing values for token replacement.
 * @returns {string} - The generated lot number string.
 */
export const generateLotNumber = (lotNumberTemplate, inboundShipment) => {
  const lotDef = parseLotNumberTemplate(lotNumberTemplate);

  const zeroPad = (num, len) => String(num).padStart(len, "0");

  const computeDateToken = (key) => {
    const now = inboundShipment?.placedDateTime
      ? new Date(inboundShipment.placedDateTime)
      : new Date();
    switch (key) {
      case "@YYYY":
        return String(now.getFullYear());
      case "@YY":
        return String(now.getFullYear()).slice(-2);
      case "@MM":
        return zeroPad(now.getMonth() + 1, 2);
      case "@QQ": {
        const quarter = Math.floor(now.getMonth() / 3) + 1; // 1..4
        return zeroPad(quarter, 2);
      }
      case "@DD":
        return zeroPad(now.getDate(), 2);
      default:
        return null;
    }
  };

  const parseModifierFromKey = (key) => {
    const lastMatch = key.match(/^@last(\d+)$/i);
    if (lastMatch) {
      return { type: "last", count: parseInt(lastMatch[1], 10) || 0 };
    }
    return null;
  };

  const applyModifier = (modifier, value) => {
    if (value == null) return value;
    const str = String(value);
    if (modifier?.type === "last" && modifier.count > 0) {
      return str.slice(-modifier.count);
    }
    return str;
  };

  const extractTokenKey = (raw) => raw.replace(/[{}]/g, "");

  const parts = [];

  for (let i = 0; i < lotDef.length; i++) {
    const element = lotDef[i];

    if (element.type === "text") {
      const next = lotDef[i + 1];
      if (next?.type === "token") {
        const nextKey = extractTokenKey(next.value);
        // If next is a modifier like @last3, look ahead one more for real token
        const modifier = parseModifierFromKey(nextKey);
        if (modifier) {
          const after = lotDef[i + 2];
          if (after?.type === "token") {
            const realKey = extractTokenKey(after.value);
            if (realKey.startsWith("@")) {
              // Modifier does not apply to date tokens — ignore modifier here
              const dateVal = computeDateToken(realKey);
              if (dateVal) {
                parts.push(element.value, dateVal);
              }
              i += 2; // consumed next (modifier) and after (date token)
            } else {
              const baseVal = resolveTokenValue(inboundShipment, realKey);
              const finalVal = baseVal
                ? applyModifier(modifier, baseVal)
                : null;
              if (finalVal) {
                parts.push(element.value, finalVal);
              }
              i += 2; // consumed next (modifier) and after (real token)
            }
          } else {
            // Modifier without a following token — skip modifier and keep label
            parts.push(element.value);
            i += 1; // consumed the modifier
          }
        } else if (nextKey.startsWith("@")) {
          // Date-like standalone token after label
          const dateVal = computeDateToken(nextKey);
          if (dateVal) {
            parts.push(element.value, dateVal);
          }
          i += 1; // consumed the date token
        } else {
          // Standard label + token pairing (skip label if token is empty)
          const tokenValue = resolveTokenValue(inboundShipment, nextKey);
          if (tokenValue) {
            parts.push(element.value, tokenValue);
          }
          i += 1; // consumed the token
        }
      } else {
        parts.push(element.value);
      }
    } else if (element.type === "token") {
      const tokenKey = extractTokenKey(element.value);
      // Modifiers act on the following token
      const modifier = parseModifierFromKey(tokenKey);
      if (modifier) {
        const next = lotDef[i + 1];
        if (next?.type === "token") {
          const realKey = extractTokenKey(next.value);
          if (!realKey.startsWith("@")) {
            const baseVal = resolveTokenValue(inboundShipment, realKey);
            const finalVal = baseVal ? applyModifier(modifier, baseVal) : null;
            if (finalVal) parts.push(finalVal);
            i += 1; // consumed the next token
          } else {
            // Ignore modifier if next is a date token
            const dateVal = computeDateToken(realKey);
            if (dateVal) parts.push(dateVal);
            i += 1; // consumed the date token
          }
        }
        // If no following token, ignore standalone modifier
      } else if (tokenKey.startsWith("@")) {
        const dateVal = computeDateToken(tokenKey);
        if (dateVal) parts.push(dateVal);
      } else {
        const tokenValue = resolveTokenValue(inboundShipment, tokenKey);
        if (tokenValue) parts.push(tokenValue);
      }
    }
  }

  // Concatenate directly; do not auto-insert spaces.
  // Trim to be safe against stray whitespace in template values.
  const lotNumber = parts.join("").replace(/\s+/g, "").trim();
  return lotNumber;
};

export function truncateText(text, maxLength = 32) {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + "...";
  }
  return text;
}

export const getInventoryItemStausLabel = (inventoryItem) => {
  return inventoryItem.status === InventoryStatus.IN_TRANSIT
    ? "Allocated"
    : "Stored";
};

export const quantityRenderer = (value, units) => {
  // Return null if value is null, undefined, or an empty string
  if (value == null || value === "") return "";

  // Format number with commas and always show two decimal places if there are any decimals
  const formattedValue = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: value % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);

  return (
    <Typography variant="body2" align="right">
      {`${formattedValue} ${units ? units : ""}`}
    </Typography>
  );
};

export const piecesRenderer = (record) => {
  const pcs =
    record?.pcs ||
    record?.product?.pcs ||
    record?.inventory?.pcs ||
    record?.inventory?.product?.pcs;

  if (pcs != null) return quantityRenderer(Number(pcs));
  return "";
};

export const availablePiecesRenderer = (record) => {
  const pcs =
    record?.pcs ||
    record?.product?.pcs ||
    record?.inventory?.pcs ||
    record?.inventory?.product?.pcs;

  const available = trimDecimal(
    (record.quantityOnHand - record.quantityCommitted) * pcs,
  );
  if (pcs != null && !isNaN(available))
    return quantityRenderer(Number(available));
  return "";
};

export const totalToPickPiecesRenderer = (record) => {
  const pcs =
    record?.pcs ||
    record?.product?.pcs ||
    record?.inventory?.pcs ||
    record?.inventory?.product?.pcs;

  const totalPcs = trimDecimal(record.totalQuantityToPick * pcs);
  if (pcs != null && !isNaN(totalPcs))
    return quantityRenderer(Number(totalPcs));
  return "";
};

export const totalPiecesRenderer = (record) => {
  const pcs = record?.pcs || record?.product?.pcs;
  if (pcs == null || pcs === "") return "";

  const total = Number(pcs) * record.quantityOnHand;
  return quantityRenderer(total);
};

// Every placement a Material-UI Tooltip accepts. Anything else has to be
// treated as "not a placement", because most callers never pass one: React
// Admin's FunctionField invokes render(record, source), and SimpleList invokes
// leftIcon(record, id), so `render={exceptionRenderer}` hands this function the
// field's source -- "hasException" -- and the list hands it a record id. Neither
// throws. Popper falls back to positioning as if it were "bottom", Material-UI
// finds no tooltipPlacement* class so the tooltip loses its offset from the
// icon, and the arrow's [x-placement] rules never match so the arrow renders
// unpositioned and invisible inside the label. Filtering here fixes every grid
// at once instead of editing a source prop out of twenty call sites.
const TOOLTIP_PLACEMENTS = [
  "bottom-end",
  "bottom-start",
  "bottom",
  "left-end",
  "left-start",
  "left",
  "right-end",
  "right-start",
  "right",
  "top-end",
  "top-start",
  "top",
];

// TODO: USE OBJECT IN PARAMS AND DESTRUCTURING IT
// Below the icon unless a caller says otherwise. The exception glyph is the
// first column of every grid that shows it, so a tooltip to its left runs off
// the page whenever the menu is collapsed.
export const exceptionRenderer = (record, position, filled = false) => {
  const title = [];
  // The whitelist doubles as the default: anything that is not a real
  // placement -- undefined included, so no default parameter to keep in
  // sync -- falls back to "bottom".
  const placement = TOOLTIP_PLACEMENTS.includes(position) ? position : "bottom";

  // Inventory Exceptinos
  if (record.overShipment)
    title.push(`Overshipment: ${record.overShipmentQuantity}`);
  if (record.underShipment)
    title.push(`Undershipment: ${record.underShipmentQuantity}`);
  if (record.isDamaged)
    title.push(
      record.originOfDamage ? `Damaged by ${record.originOfDamage}` : "Damaged",
    );
  if (record.isRecalled) title.push(`Recalled`);
  if (record.otherException) title.push(`Other Exception`);
  if (record?.barcodeException) {
    title.push(record?.barcodeExceptionNote);
  }
  if (record?.otherExceptionNote) {
    title.push(record?.otherExceptionNote);
  }

  // Shipment Exceptions
  if (record.unsafeShipment) title.push(`Unsafe Shipment`);
  if (record.brokenSeal) title.push(`Broken Seal`);

  // Return Shipment (not an exception, but important status indicator).
  // Pushed before the damage check below: only one glyph can be drawn, and a
  // record that is both damaged and a return still has to say so in the
  // tooltip, whichever icon wins.
  if (record.returnShipment) title.push(`Return Shipment`);

  // Damage outranks every other exception. Damaged stock must never be picked
  // by mistake, so it gets its own red glyph instead of the amber one shared by
  // over/undershipment, recall and other exceptions. Only Inventory and
  // InventoryItem carry isDamaged, so shipment rows are unaffected.
  if (record.isDamaged)
    return (
      <Tooltip
        arrow
        placement={placement}
        title={title.filter(Boolean).join("\n")}
      >
        <span style={{ display: "inline-flex" }}>
          <DamagedIcon style={{ color: DAMAGED_COLOR }} />
        </span>
      </Tooltip>
    );

  if (record.returnShipment)
    return (
      <Tooltip
        arrow
        placement={placement}
        title={title.filter(Boolean).join("\n")}
      >
        <span style={{ display: "inline-flex" }}>
          <ReturnIcon style={{ color: orange[600] }} />
        </span>
      </Tooltip>
    );

  if (record.hasException)
    return (
      <Tooltip
        arrow
        placement={placement}
        title={title.filter(Boolean).join("\n")}
      >
        <span style={{ display: "inline-flex" }}>
          {filled ? (
            <WarningIcon style={{ color: orange[600] }} />
          ) : (
            <ExceptionIcon style={{ color: orange[600] }} />
          )}
        </span>
      </Tooltip>
    );
  return <></>;
};

export const renderFullName = (record) => {
  return `${record.lastName}, ${record.firstName}`;
};

export const taxRateRenderer = (choice) => {
  if (
    choice.taxCode === AstTaxCode.EXCEMPT ||
    choice.taxCode === AstTaxCode.TAXABLE
  ) {
    return `${choice.name ? choice.name : choice.taxCode}`;
  } else {
    return `${choice.name ? choice.name : choice.taxCode} : ${choice.taxRate}%`;
  }
};

export const getBarcodesString = (sealItems) => {
  if (!sealItems || sealItems?.length === 0) return null;
  const barcodes = sealItems.map((seal) => seal.barcode).join(",");
  return barcodes;
};

/**
 * Rounds and Trims a decimal from zero to the max of specified number of digits after decimal.
 *
 * @param {number} number - The number to be rounded.
 * @param {number} [decimalPlaces=2] - The max number of decimal places to keep after rounding (default: 2).
 *
 * @returns {number} The rounded number as a float.
 */
export const trimDecimal = (number, decimalPlaces = 2) => {
  return Math.round(number * 10 ** decimalPlaces) / 10 ** decimalPlaces;
};

/**
 * The car's real Location id, or "" when it has none. The Unspotted lane is a
 * virtual track, not a Location, so a car parked there has nowhere to place
 * inventory — callers that look up a Location must go through this.
 */
export const getSpottedTrackId = (shipment) => {
  const trackId = shipment?.trackCar?.trackId;
  return !trackId || trackId === UNSPOTTED_TRACK_ID ? "" : trackId;
};

export const isInboundCarNotSpotted = (shipment) =>
  !getSpottedTrackId(shipment);

/**
 * States where the crew still works the railcar itself. A car can be pulled
 * back to Unspotted at any point — including after the shipment was accepted —
 * so this is not limited to the accept step. Review and completed inbounds are
 * past it: their car is usually already pulled.
 *
 * Collecting a car also clears its trackId, so without the collected check
 * every departed railcar reads as unspotted and the crew is sent to Track
 * Management for a car that is gone. `collectedDateTime` is checked alongside
 * `isCollected` because the crew query (getInboundShipmentMobile) selects only
 * the former.
 */
export const needsRailcarSpotting = (shipment) =>
  isRailShipmentInTrackArea(shipment.areaId, shipment) &&
  !(shipment.collectedDateTime || shipment.isCollected) &&
  isInboundCarNotSpotted(shipment) &&
  (shipment.state === InboundState.PROCESS_SHIPMENT ||
    shipment.state === InboundState.APPROVE_SHIPMENT ||
    shipment.state === InboundState.PROCESS_INVENTORY ||
    shipment.state === InboundState.APPROVE_INVENTORY);

export const getInboundCurrentStepName = (currentShipment) => {
  if (isRailShipmentInTrackArea(currentShipment.areaId, currentShipment)) {
    const inAcceptStep =
      currentShipment.state === InboundState.PROCESS_SHIPMENT ||
      currentShipment.state === InboundState.APPROVE_SHIPMENT;
    // Only the accept step rewinds the stepper to "Spot Railcar" — this value
    // is persisted onto the shipment, so a car pulled back to Unspotted mid
    // inventory must not rewrite its recorded progress.
    if (needsRailcarSpotting(currentShipment) && inAcceptStep)
      return InboundTrackSteps[0];
    if (inAcceptStep) return InboundTrackSteps[1];
    if (
      currentShipment.state === InboundState.PROCESS_INVENTORY ||
      currentShipment.state === InboundState.APPROVE_INVENTORY
    ) {
      return InboundTrackSteps[2];
    }
    return InboundTrackSteps[3];
  } else {
    if (
      currentShipment.state === InboundState.PROCESS_SHIPMENT ||
      currentShipment.state === InboundState.APPROVE_SHIPMENT
    ) {
      return InboundTrackSteps[1];
    }
    if (
      currentShipment.state === InboundState.PROCESS_INVENTORY ||
      currentShipment.state === InboundState.APPROVE_INVENTORY
    ) {
      return InboundTrackSteps[2];
    }
    return InboundTrackSteps[3];
  }
};

export function extractCounter(pickupNumber) {
  // Split the string by dash '-'
  const parts = pickupNumber.split("-");
  // The counter is the last element in the array
  const counter = parts[parts.length - 1];
  return counter;
}

export const cleanUrlLink = (url) => {
  if (!url) return url;

  // Parse the URL with a dummy base URL to handle relative URLs
  // The dummy base URL ensures the URL constructor can parse relative URLs correctly
  const urlObj = new URL(url, "https://dummy");
  const path = urlObj.pathname; // Extract the pathname from the URL
  const queryParams = urlObj.searchParams; // Extract the search parameters from the URL

  // Split the path into segments and filter out empty segments
  const segments = path.split("/").filter((segment) => segment);

  // Regular expression pattern to match UUIDs
  const uuidPattern =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  // Transform segments, replacing UUIDs with '{id}' and decoding URI components
  const transformedSegments = segments.map((segment) => {
    if (uuidPattern.test(segment)) {
      return "{id}";
    }
    return decodeURIComponent(segment); // Decode URI component
  });

  // Add query parameter values to the transformed segments after decoding
  for (const value of queryParams.values()) {
    transformedSegments.push(decodeURIComponent(value));
  }

  // Join the transformed segments to form the cleaned URL
  const cleanUrl = "/" + transformedSegments.join("/");
  return cleanUrl;
};

export const findFirstHrefFromChildren = (children) => {
  if (Array.isArray(children) && children.length >= 1) {
    return children.find((child) => child?.props?.href)?.props?.href;
  }
  return null;
};

export function isBarcodeRequired(account, inventory) {
  return inventory.product.isBarcodeRequired || account.isBarcodeRequired;
}

export function getLastValueFromUrl(url) {
  const parts = url.split("/").filter((part) => part !== "");
  let lastPart = parts[parts.length - 1];
  if (lastPart.includes("?"))
    lastPart = lastPart.substring(0, lastPart.indexOf("?"));
  return lastPart;
}

export const getInitialTab = (horizontalTabPath) => {
  const currentPath = getLastValueFromUrl(window.location.pathname);
  if (currentPath === horizontalTabPath) {
    const searchParams = new URLSearchParams(window.location.search);
    const t = searchParams.get("t");
    const parsed = parseInt(t);
    return !isNaN(parsed) ? parsed : 0;
  }
  return 0;
};

let audioContext = null;

export const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new AudioContext();
  }
  return audioContext;
};

export function beep(vol, freq, duration) {
  const audioContext = getAudioContext();
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.connect(gain);
  oscillator.frequency.value = freq;
  oscillator.type = "square";
  gain.connect(audioContext.destination);
  gain.gain.value = vol * 0.01;
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration * 0.001);
}

export const defaultNumberFormat = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

export const isChildNull = (children) => {
  if (!children) return true;
  if (React.isValidElement(children)) {
    const { type, props } = children;
    if (typeof type === "function") {
      const renderedOutput = type(props);
      return (
        renderedOutput === null ||
        renderedOutput === false ||
        (renderedOutput &&
          renderedOutput.props &&
          Object.keys(renderedOutput.props).length === 0)
      );
    }
    return type === "div" && Object.keys(props).length === 0;
  }
  return false;
};

export const getYardLogo = () =>
  !window?.appUser?.reloadYard?.logoImage
    ? ""
    : getPhotoUrl(window.appUser.reloadYard.logoImage.key);

export const getReloadYardAddress = () => ({
  city: window?.appUser?.reloadYard?.city || "",
  country: window?.appUser?.reloadYard?.country || "",
  fax: window?.appUser?.reloadYard?.fax || "",
  phone: window?.appUser?.reloadYard?.phone || "",
  state: window?.appUser?.reloadYard?.state || "",
  street: window?.appUser?.reloadYard?.street || "",
  timeZone: window?.appUser?.reloadYard?.timeZone || "",
  zipCode: window?.appUser?.reloadYard?.zipCode || "",
  yardLogo: getYardLogo(),
  yardName: window?.appUser?.reloadYard?.name,
  yardAddress1: `${window?.appUser?.reloadYard?.state}, ${window?.appUser?.reloadYard?.city}, ${window?.appUser?.reloadYard?.street}, ${window?.appUser?.reloadYard?.zipCode}`,
  yardAddress2: "N/A",
  yardPhoneNo: window?.appUser?.reloadYard?.phone,
  yardURL: window?.appUser?.reloadYard?.website,
});

export const formatCurrency = (amount) =>
  Number(amount ?? 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    // minimumFractionDigits: 2,
    // maximumFractionDigits: 2,
  });

export function formatPhoneNumber(raw) {
  if (!raw) return "";

  const digits = raw.replace(/\D/g, "");

  if (digits.length === 10) {
    // Format: 123-123-1234
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    // Format: 1-123-123-1234
    return `${digits[0]}-${digits.slice(1, 4)}-${digits.slice(
      4,
      7,
    )}-${digits.slice(7)}`;
  }

  // Fallback: group into 3-3-4-...
  const groups = [];
  let i = 0;
  while (i < digits.length) {
    const chunkSize = i === 6 ? 4 : i < 6 ? 3 : 4; // First 6 digits split 3-3, then 4s
    groups.push(digits.slice(i, i + chunkSize));
    i += chunkSize;
  }

  return groups.join("-");
}

export const areAllSealsValid = (advisedSeals, enteredSeals) => {
  if (!advisedSeals && !enteredSeals) {
    return true;
  }
  const updatedSeals = enteredSeals
    .filter((el) => el.validated)
    .map((el) => el.barcode);
  function arraysEqualIgnoreOrder(arr1, arr2) {
    if (arr1?.length !== arr2?.length) return false;

    // Sort the arrays
    const sortedArr1 = [...arr1].sort();
    const sortedArr2 = [...arr2].sort();

    // Compare the sorted arrays
    return sortedArr1.every((value, index) => value === sortedArr2[index]);
  }
  if (
    updatedSeals.length === enteredSeals.length &&
    arraysEqualIgnoreOrder(updatedSeals, advisedSeals)
  ) {
    return true;
  } else {
    return false;
  }
};

export const isShipmentUnits = (uom) => {
  return [Unit.CAR, Unit.TL].includes(uom);
};
